import { storeToRefs } from 'pinia'
import {
  forwardAppMessage,
  deviceManagementPrintService,
  useDeviceManagementStore,
} from '@nsftx/seven-client-retail-vue-dm'
import { logService } from '@nsftx/seven-client-retail-utils'
import {
  printService,
  DEFAULT_PRINT_CURRENCY,
  type PrintContext,
  type PrintOptions,
  type PrintGlobalsBasic,
  RETAIL_PRINT_JOB_DM_MESSAGE_TYPE,
  isDmAppMessageForwardEnabled,
} from '@nsftx/seven-client-retail-print'
import { getNumberFormat } from '@nsftx/seven-nxg-retail-vue-sdk'
import { cmsService } from '@nsftx/seven-client-retail-vue-sgp'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'

const LOG_PREFIX = '[printService]'

let printServiceInstance: null | ReturnType<typeof printService.createPrint>

const initPrintService = () => {
  const { cmsConfig } = storeToRefs(useCmsConfigStore())
  const { appData } = storeToRefs(useDeviceManagementStore())
  const config = cmsConfig.value

  const globals: PrintGlobalsBasic = {
    location: {
      name: appData.value?.betshop.name || 'Unknown Betshop',
    },
    device: {
      name: appData.value?.device.name || 'Unknown Device',
    },
  }
  const spaces: PrintOptions['spaces'] = Object.fromEntries(
    Object.entries(cmsService.createSpacesFromTemplates(config?.templates.docs ?? [])).map(
      ([spaceName, space]) => [
        spaceName,
        {
          compilerOptions: {
            engineConfig: {
              templates: space.templates,
              globals,
            },
          },
        },
      ],
    ),
  )
  const { group, decimal, fractionDigits } = getNumberFormat()
  const options: PrintOptions = {
    driver: {
      getPrinterStatus: deviceManagementPrintService.getPrinterStatus,
      addJob: <T>(compiledTemplate: string, options: PrintContext<T>, rawTemplate?: string) => {
        if (isDmAppMessageForwardEnabled()) {
          forwardAppMessage(RETAIL_PRINT_JOB_DM_MESSAGE_TYPE, {
            html: compiledTemplate,
            rawTemplate: rawTemplate ?? compiledTemplate,
            source: 'pos',
            data: options,
          })
        }
        logService.debug(`${LOG_PREFIX} Passing print job to DM API`, {
          code: 'SS_PRINT_ADD_PRINT_JOB',
        })
        return deviceManagementPrintService.addPrintJob(compiledTemplate, options)
      },
    },
    spaces,
    checkStatusBeforePrint: false,
    checkStatusAfterPrint: false,
    numberFormat: { group, decimal, fractionDigits },
    currency: DEFAULT_PRINT_CURRENCY,
  }

  printServiceInstance = printService.createPrint(options)
}

const getPrintService = () => {
  if (!printServiceInstance) {
    throw new Error('Print service not initialized. Please call initPrintService first.')
  }

  return printServiceInstance
}

const addOperatorDataToGlobals = (operatorName: string) => {
  if (!printServiceInstance) {
    throw new Error('Print service not initialized. Please call initPrintService first.')
  }

  printServiceInstance.updateGlobals('pos', {
    operator: {
      name: operatorName,
    },
  })
}

export default {
  initPrintService,
  getPrintService,
  addOperatorDataToGlobals,
}
